import Footer from "@/components/layout/Footer";
import { Navigation } from "@/components/layout/Navigation";
import { Badge } from "@/components/ui/badge";
import Hero from "@/components/marketing/Hero";


export default function TermsOfService() {

  return (
    <div className="min-h-screen">
      <Navigation showAuth={true} className="mt-4" />

      <Hero
        badge="Terms"
        headline="Terms of Service"
        description="Legal terms for using Arcitext products and services"
      />

      <div className="pb-16">
        <div className="prose prose-slate max-w-3xl mx-auto">
          <p className="text-text-muted">
            Last updated: October 7, 2025
          </p>

          {/* 1. Introduction */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">1</span>
            Introduction
          </h2>
          <p>
            Welcome to Arcitext. These Terms of Service ("Terms") govern your use of our website, products, and services.
            By accessing or using Arcitext, you agree to be bound by these Terms and our Privacy Policy.
          </p>
          <p>
            Arcitext provides AI-assisted copywriting tools for people that write.
            Our products include RSS aggregation, copywriting tools, and analysis tools designed to enhance creative copywriting workflows.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "These Terms create a legally binding agreement between you and Arcitext" },
                { text: "By using our products, you accept these Terms and our Privacy Policy" },
                { text: "You must be at least 18 years old to use our products" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 2. Who Can Use Arcitext */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">2</span>
            Who Can Use Arcitext
          </h2>
          <p>
            You must be at least 18 years old to use our products. By using Arcitext, you represent and warrant that:
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li>You are at least 18 years old</li>
            <li>You have the legal capacity to enter into this agreement</li>
            <li>Your use of Arcitext will not violate any applicable laws or regulations</li>
            <li>The information you provide to us is accurate and complete</li>
          </ul>
          <p>
            If you are using Arcitext on behalf of a company or organization, you represent and warrant that you have the authority to bind that entity to these Terms.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "You must be at least 18 years old to use Arcitext" },
                { text: "You need legal capacity to enter into this agreement" },
                { text: "When representing an organization, you must have authority to bind that entity" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 3. Digital Products and Licensing */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">3</span>
            Digital Products and Licensing
          </h2>
          <p>
            Arcitext offers digital products and subscription services that grant you a non-exclusive, non-transferable license for your own internal business purposes. You receive access to the service features you have purchased, but no ownership interest in the underlying software or content.
          </p>
          <p>
            All purchases are non-refundable. If you have a billing question or believe a charge is incorrect, contact <a href="mailto:billing@arcitext.com" className="text-accent-indigo hover:text-accent-indigo/80">billing@arcitext.com</a> and we will review the request.
          </p>
          <p>
            You may not:
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li>Redistribute, sell, or sublicense the product or any portion of the service</li>
            <li>Remove any copyright or proprietary notices from the service</li>
            <li>Use the service in a way that violates applicable laws or regulations</li>
            <li>Reverse engineer, decompile, or disassemble the service</li>
          </ul>
          <p>
            Any outputs generated through Arcitext in conjunction with third-party services (such as OpenAI models) remain subject to those services' terms and conditions.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "Your license grants access for internal business use only" },
                { text: "All purchases are non-refundable—email billing@arcitext.com for billing help" },
                { text: "Redistributing or sublicensing Arcitext is prohibited" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 4. Acceptable Use */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">4</span>
            Acceptable Use
          </h2>
          <p>
            You agree to use Arcitext products and services only for lawful purposes and in accordance with these Terms. You agree not to use our products:
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li>In any way that violates any applicable law or regulation</li>
            <li>To generate content that is harmful, offensive, or violates the rights of others</li>
            <li>To attempt to gain unauthorized access to our systems or user accounts</li>
            <li>To engage in any activity that interferes with or disrupts our services</li>
            <li>To transmit any viruses, malware, or other harmful code</li>
            <li>To import RSS feeds or other materials for which you do not have the necessary licenses or permissions</li>
          </ul>
          <p>
            We reserve the right to terminate or suspend your access to our products if you violate these acceptable use provisions or any other part of these Terms.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "You must use our products only for lawful purposes" },
                { text: "Don't create harmful or offensive content with our tools" },
                { text: "We can terminate your access if you violate these terms" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 5. Intellectual Property */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">5</span>
            Intellectual Property
          </h2>
          <p>
            All content, features, and functionality of Arcitext, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, are the exclusive property of Arcitext, its licensors, or other content suppliers and are protected by international copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Arcitext.
          </p>
          <p>
            When you provide feedback, ideas, or suggestions about our products ("Feedback"), you grant us a non-exclusive, royalty-free, perpetual, irrevocable, and fully sublicensable right to use, reproduce, modify, adapt, publish, translate, create derivative works from, distribute, and display such Feedback throughout the world in any media.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "All Arcitext content and products are protected by intellectual property laws" },
                { text: "You cannot use our trademarks without permission" },
                { text: "We can use any feedback you provide about our products" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 6. Privacy */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">6</span>
            Privacy
          </h2>
          <p>
            Your privacy is important to us. Our <a href="/privacy" className="text-accent-indigo hover:text-accent-indigo/80">Privacy Policy</a> explains how we collect, use, and protect your personal information when you use our website and products.
          </p>
          <p>
            By using Arcitext, you consent to the collection and use of information as described in our Privacy Policy. If you do not agree with our Privacy Policy, please do not use our services.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "Our Privacy Policy explains how we handle your data" },
                { text: "Using our services means you consent to our data practices" },
                { text: "You can read our full Privacy Policy at Arcitext.com/privacy" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 7. Liability Disclaimer */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">7</span>
            Liability Disclaimer
          </h2>
          <p>
            Arcitext products are provided "as is" and "as available" without any warranties of any kind, either express or implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
          </p>
          <p>
            We do not guarantee that our products will meet your requirements or be available on an uninterrupted, secure, or error-free basis. We make no warranty regarding the quality, accuracy, timeliness, truthfulness, completeness, or reliability of any content available through our services.
          </p>
          <p>
            To the maximum extent permitted by applicable law, in no event shall Arcitext, its affiliates, or their respective directors, employees, or agents be liable for any indirect, punitive, incidental, special, consequential, or exemplary damages, including without limitation damages for loss of profits, goodwill, use, data, or other intangible losses, that result from the use of, or inability to use, our services.
          </p>
          <p>
            Arcitext is not responsible for any loss, corruption, or deletion of user-generated content, including but not limited to written texts, analyzes, RSS entries, or AI-assisted responses. We strongly encourage you to maintain backups of any important materials.
          </p>
          <p>
            Our liability is limited to the maximum extent permitted by law and shall not exceed the amount you paid for the product giving rise to such liability.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "Our products are provided 'as is' without warranties" },
                { text: "We're not liable for indirect or consequential damages" },
                { text: "Our maximum liability is limited to what you paid for the product" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 8. Changes to These Terms */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">8</span>
            Changes to These Terms
          </h2>
          <p>
            We may update these Terms from time to time to reflect changes in our products, practices, or laws. We will notify you of any material changes by posting the new Terms on this page and updating the "Last updated" date.
          </p>
          <p>
            Your continued use of Arcitext after we post changes to the Terms means that you accept and agree to the revised Terms. If you do not agree to the revised Terms, please stop using our services.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We may update these Terms from time to time" },
                { text: "Material changes will be posted on this page with an updated date" },
                { text: "Continued use after changes means you accept the new Terms" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 9. Contact */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">9</span>
            Contact
          </h2>
          <p>
            If you have any questions about these Terms, please contact us at
            <a href="mailto:hello@arcitext.com" className="text-accent-indigo hover:text-accent-indigo/80"> hello@arcitext.com</a>.
          </p>

          <h2 className="mt-8">Company details:</h2>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>Legal name:</strong> Fjordkit Digital</li>
            <li><strong>Business address:</strong> Vissevej 121, 9210 Aalborg SØ, Denmark</li>
            <li><strong>Email:</strong> <a href="mailto:hello@arcitext.com" className="text-accent-indigo hover:text-accent-indigo/80">hello@arcitext.com</a></li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
}
